/* eslint-disable @typescript-eslint/naming-convention */
import MultiSelectInputFreeSolo from '@brands/Components/Inputs/MuiMultiSelectInputFreeSolo/MultiSelectInputFreeSolo';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import PatientIntakeNavigation from '@brands/Components/PatientIntakeNavigation/PatientIntakeNavigation';
import { MedicalHistorySubmit } from '@brands/Dashboard/MedicalHistory/MedicalHistory/utils/types';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { addClinicalData } from '@brands/services/clinical_data/addClinicalData';
import { listClinicalData } from '@brands/services/clinical_data/listClinicalData';
import { MedicalHistory } from '@brands/services/clinical_data/types/types';
import { updateClinicalData } from '@brands/services/clinical_data/updateClinicalData';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { displayErrorDetails } from '@brands/Utils/displayError';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Button } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Container from '../../../../../Components/Container/Container';
import MuiToggle from '../../../../../Components/Inputs/MuiToggle/MuiToggle';
import { allergyAndMedicationOptions } from '../../../../MedicalHistory/MedicalHistory/utils/questions';
import { useMedicalHistoryOptional } from '@brands/Dashboard/PatientForms/PatientForm/utils/clinicalEnablementFlow';
import { sanitizeMedicalHistoryBooleans } from '../utils/medicalHistoryDefaults';
import styles from './styles.module.scss';

const ClinicalDataStepFive = (): JSX.Element => {
  const screenSize = useWindowSize();
  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const { values, selectedPatient } = useAppSelector(selectPatientForm);
  const selectedPatientId = selectedPatient?.value;
  const bypassRequired = useMedicalHistoryOptional(values);

  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const { state, pathname } = useLocation();
  const isIntakeProcess = useMemo(() => {
    return state?.isIntakeProcess || pathname.includes('clinical-data') || false;
  }, [state]);

  const [loading, setLoading] = useState(true);
  const [historyObjectId, setHistoryObjectId] = useState<number | null>(null);
  const [allergicToMedications, setAllergicToMedications] = useState<boolean | null>(null);
  const [medicationsAllergies, setMedicationsAllergies] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Tracks whether the patient actually edited this step, so we only confirm a
  // real save instead of showing a misleading toast on plain navigation.
  const userEditedRef = useRef(false);

  const [medicalHistory, setMedicalHistory] = useState<MedicalHistory>({
    id: 0,
    under_care_of_physician: null,
    conditions_treated: null,
    taking_medication: null,
    medications: null,
    allergic_to_medications: null,
    medications_allergies: null,
    using_substances: [],
    interested_in_stopping: null,
    height: '',
    weight: '',
    pharmacy_name: null,
    pharmacy_street_address: null,
    pharmacy_city: null,
    pharmacy_state: null,
    pharmacy_zip_code: null,
    pharmacy_phone_number: null,
  } as MedicalHistory);

  const saveFormData = useCallback((): void => {
    if (hasUnsavedChanges && !loading) {
      const currentValues = {
        allergic_to_medications: allergicToMedications,
        medications_allergies: medicationsAllergies,
      };
      sessionStorage.setItem('allergies', JSON.stringify(currentValues));
    }
  }, [hasUnsavedChanges, allergicToMedications, medicationsAllergies, loading]);

  const onSubmit = async (): Promise<void> => {
    if (bypassRequired && allergicToMedications === null) {
      sessionStorage.removeItem('allergies');
      roleNavigate('/clinical-data/step-six');
      return;
    }

    try {
      setLoading(true);
      setHasUnsavedChanges(false);
      const medicalHistoryPayload: MedicalHistorySubmit = {
        user_id: selectedPatientId as number,
        ...medicalHistory,
        allergic_to_medications: allergicToMedications,
        medications_allergies: allergicToMedications ? medicationsAllergies : null,
      };

      const sanitizedPayload = sanitizeMedicalHistoryBooleans(medicalHistoryPayload, bypassRequired);
      if (!historyObjectId) {
        await addClinicalData('medical_history', sanitizedPayload);
      } else {
        await updateClinicalData('medical_history', historyObjectId.toString(), sanitizedPayload);
      }

      if (userEditedRef.current && !isIntakeProcess) {
        showSnackbar('Medical history saved successfully!', 'success');
      }
      sessionStorage.removeItem('allergies');
      setLoading(false);
      roleNavigate('/clinical-data/step-six');
    } catch (error) {
      displayErrorDetails(error);
      setHasUnsavedChanges(true);
    }
  };

  useEffect(() => {
    let isActive = true;

    const hydrateClinicalData = async (): Promise<void> => {
      if (!selectedPatientId) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const savedDraft = sessionStorage.getItem('allergies');
        const res = await listClinicalData(selectedPatientId);

        if (!isActive) return;

        if (res.medical_history) {
          setHistoryObjectId(res.medical_history.id || null);
          setMedicalHistory(res.medical_history);
        }
        const parsedData = savedDraft ? JSON.parse(savedDraft) : res.medical_history;

        setAllergicToMedications(parsedData?.allergic_to_medications ?? null);
        setMedicationsAllergies(parsedData?.medications_allergies || null);
      } catch (error) {
        if (isActive) {
          displayErrorDetails(error);
        }
      } finally {
        if (isActive) {
          setLoading(false);
          setHasUnsavedChanges(true);
        }
      }
    };

    hydrateClinicalData();

    return () => {
      isActive = false;
    };
  }, [selectedPatientId]);

  useEffect(() => {
    return () => {
      saveFormData();
    };
  }, [saveFormData]);

  useEffect(() => {
    // Handle page refresh/close
    const handleBeforeUnload = (): void => {
      saveFormData();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saveFormData]);

  if (loading) {
    return <Loading fullScreen />;
  }

  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        overflow: 'hidden',
      }}
      childrenStyle={{
        zIndex: '2',
        paddingTop: screenSize.width > 768 ? '40px' : '16px',
        paddingBottom: screenSize.width > 768 ? '40px' : '16px',
      }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={`fs-unmask ${styles.clinicalDataIntroContainer}`}>
        <PatientIntakeNavigation progress={36} />
        <form className={`${styles.formContainer}`}>
          <div className={styles.clinicalDataIntroWrapper}>
            <div>
              <div className={styles.clinicalDataIntroTitle}>
                <span>Are you allergic to any medications, metal, latex, or certain materials?</span>
                {!bypassRequired && <span className={styles.clinicalDataIntroTitleRequired}> *</span>}
              </div>
            </div>
            <div className={styles.clinicalDataIntroBody} style={{ paddingBottom: '0px' }}>
              <div className={styles.formContent}>
                <MuiToggle
                  label="Are you allergic to any medications, metal, latex, or certain materials?"
                  hideLabel
                  options={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                  ]}
                  selectedValue={allergicToMedications === null ? '' : allergicToMedications ? 'yes' : 'no'}
                  onChange={(val) => {
                    userEditedRef.current = true;
                    setAllergicToMedications(val === 'yes');
                    if (val === 'no') {
                      setMedicationsAllergies(null);
                    }
                  }}
                />
              </div>
            </div>
            {allergicToMedications && (
              <div className={styles.clinicalDataIntroBody}>
                <div className={styles.formContent}>
                  <label className={styles.clinicalDataIntroLabel} htmlFor="medicationsAllergies">
                    Search allergies
                  </label>
                  <MultiSelectInputFreeSolo
                    label="Search allergies"
                    errors={{}}
                    name="medicationsAllergies"
                    onChange={(selectedValues: string[]) => {
                      userEditedRef.current = true;
                      setMedicationsAllergies(selectedValues.join(', '));
                      setMedicalHistory({ ...medicalHistory, medications_allergies: selectedValues.join(', ') });
                    }}
                    hideLabel
                    styledTag
                    displayCheckbox={false}
                    value={medicationsAllergies ? medicationsAllergies.split(',').map((med) => med.trim()) : []}
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfileLanguage}`}
                    options={allergyAndMedicationOptions}
                  />
                </div>
              </div>
            )}
            <div className={styles.clinicalDataIntroFooter}>
              <Button variant="text" className={styles.clinicalDataIntroFooterBack} onClick={() => navigate(-1)}>
                <ArrowBackIosNewIcon />
                Back
              </Button>
              <Button
                variant="contained"
                className={styles.clinicalDataIntroFooterNext}
                type="submit"
                disabled={
                  loading ||
                  (!bypassRequired &&
                    (allergicToMedications === null ||
                      (allergicToMedications === true &&
                        (!medicationsAllergies || medicationsAllergies.trim() === ''))))
                }
                onClick={(e) => {
                  e.preventDefault();
                  if (allergicToMedications !== null || bypassRequired) {
                    onSubmit();
                  } else {
                    showSnackbar('Please fill in all required fields before proceeding.', 'error');
                  }
                }}
              >
                Next
              </Button>
            </div>
          </div>
        </form>
      </div>
      <GenericSnackbar open={snackbar.isOpen} message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} />
    </Container>
  );
};
export default ClinicalDataStepFive;
